// Eligibility design tokens. Darkened from MUI defaults to hit 4.5:1 contrast
// on the soft/tint backgrounds — do not substitute theme.palette.warning.main etc.
// Source: eligibility-prototype/src/styles.css :root.

import type { BlockReadiness } from '../types/readiness';

export const ELIG_TOKENS = {
  midnight: '#1C1E85',
  ink: '#14161A',
  inkSoft: '#4B5563',
  inkFaint: '#9098A3',
  bg: '#FAFAF8',
  card: '#FFFFFF',
  line: 'rgba(28,30,133,0.08)',
  lineStrong: 'rgba(28,30,133,0.14)',

  amber: '#92400E',
  amberSoft: 'rgba(146,64,14,0.10)',
  amberTint: 'rgba(146,64,14,0.04)',

  red: '#B91C1C',
  redSoft: 'rgba(185,28,28,0.10)',
  redTint: 'rgba(185,28,28,0.04)',

  green: '#047857',
  greenSoft: 'rgba(4,120,85,0.10)',
  greenTint: 'rgba(4,120,85,0.04)',

  gray: '#4B5563',
  graySoft: 'rgba(75,85,99,0.10)',
  grayTint: 'rgba(75,85,99,0.04)',
} as const;

export type EligibilityTone = 'amber' | 'red' | 'green' | 'gray';

// readiness → tone. Single source for stripe color, dot color, tint selection.
export const READINESS_TONE: Record<BlockReadiness, EligibilityTone> = {
  verified: 'green',
  collect: 'green',
  collected: 'green',
  attention: 'amber',
  blocked: 'red',
  info: 'gray',
};

// Filled = verdict reached AND no action pending.
// Hollow = verdict reached BUT action pending, or info gap.
export const READINESS_FILLED: Record<BlockReadiness, boolean> = {
  verified: true,
  collected: true,
  blocked: true,
  collect: false,
  attention: false,
  info: false,
};

// 0px → no decoration · 3px → workflow attention, visit IS happening
// 12px → visit itself is in question
export const READINESS_STRIPE_PX: Record<BlockReadiness, 0 | 3 | 12> = {
  verified: 0,
  collected: 0,
  info: 0,
  collect: 3,
  attention: 3,
  blocked: 12,
};

// Generic readiness derivation. Pure — no React. Surfaces compose this with
// their own visual primitives (calendar's stripe CSS vars, badge's dot, etc.).
export interface ReadinessTreatment {
  tone: EligibilityTone;
  color: string;
  filled: boolean;
  stripeWidthPx: 0 | 3 | 12;
}

export const getReadinessTreatment = (
  readiness: BlockReadiness | null,
): ReadinessTreatment | null => {
  if (readiness == null) return null;
  const tone = READINESS_TONE[readiness];
  return {
    tone,
    color: ELIG_TOKENS[tone],
    filled: READINESS_FILLED[readiness],
    stripeWidthPx: READINESS_STRIPE_PX[readiness],
  };
};
